import { useApolloClient } from "@apollo/client/react";
import { createPostAssociation, deletePostAssociation } from "deso-protocol";
import { useContext, useRef, useState } from "react";

import { createToastErrorDetails } from "@/components/ui/toast-helpers";
import { useToast } from "@/components/ui/use-toast";
import { FeedContext } from "@/contexts/feed-context";
import { usePostContext } from "@/contexts/post-context";
import { UserContext } from "@/contexts/user-context";
import type {
  PostAssociation,
  PostByPostHashStatsQuery,
  PostStat,
} from "@/graphql/codegen/graphql";
import { useConfetti } from "@/hooks/use-confetti";
import { useGetCurrentUserPostStats } from "@/hooks-api/use-get-current-user-post-stats";
import {
  AssociationReactionValue,
  AssociationReactionValueEmojiMap,
  AssociationType,
} from "@/utils/constants";

type ReactionLogicDTO = {
  postHash: string;
};

const ReactionTypeToFieldNameMap: Record<
  AssociationReactionValue,
  keyof PostStat
> = {
  [AssociationReactionValue.LIKE]: "likeReactionCount",
  [AssociationReactionValue.DISLIKE]: "dislikeReactionCount",
  [AssociationReactionValue.LAUGH]: "laughReactionCount",
  [AssociationReactionValue.SAD]: "sadReactionCount",
  [AssociationReactionValue.ASTONISHED]: "astonishedReactionCount",
  [AssociationReactionValue.ANGRY]: "angryReactionCount",
  [AssociationReactionValue.LOVE]: "loveReactionCount",
};

const SYNC_THROTTLE_TIMEOUT = 3000;

// Enable this in case you need to debug reaction, it'll help understand the flow and what's happening
const DEBUG_ENABLED = false;
const debug = (...args: unknown[]) => {
  if (DEBUG_ENABLED) {
    console.log("[useReactionLogic]", ...args);
  }
};

export const useReactionLogic = ({ postHash }: ReactionLogicDTO) => {
  const { toast } = useToast();
  const {
    user,
    wealth,
    globalVal: { maxPostContributionWealthUsdCents },
  } = useContext(UserContext);
  const [, setSyncing] = useState(false);
  const { posts, updatePostByHash } = useContext(FeedContext);
  const { post, repost, isPureRepost, updatePost } = usePostContext();
  const updatedPostRef = useRef<typeof post | null>(post);

  const {
    data,
    loading: reactionsLoading,
    setOptimistic,
    fetchOnly,
    readLatestData,

    upvoteWealthTotalUsdCentsCapped,
    totalReactionsCount,
  } = useGetCurrentUserPostStats(isPureRepost ? repost : post);
  const apolloClient = useApolloClient();
  const { addConfetti } = useConfetti();

  const currentUserStats = data?.postByPostHash;

  const userReactions =
    currentUserStats?.currentUserReactions?.nodes.filter(Boolean) ?? [];
  const totalCount = totalReactionsCount;

  const userCappedWealth = Math.min(
    wealth?.totalBalanceUsdCents ?? 0,
    maxPostContributionWealthUsdCents,
  );

  const getUserReactions = () =>
    (readLatestData()?.postByPostHash?.currentUserReactions?.nodes?.filter(
      Boolean,
    ) ?? []) as PostAssociation[];

  const getLatestReactions = () =>
    Object.fromEntries(
      Object.keys(AssociationReactionValue).map((key) => [
        key,
        {
          isReacted: !!userReactions.find(
            (association) => association?.associationValue === key,
          ),
        },
      ]),
    );

  const handleOptimisticUpdate = ({
    associationValue,
  }: {
    associationValue: AssociationReactionValue;
  }) => {
    const oldCurrentUserStats = readLatestData()?.postByPostHash;

    if (!oldCurrentUserStats) return;

    const newCurrentUserStats = structuredClone(
      oldCurrentUserStats,
    ) as NonNullable<PostByPostHashStatsQuery["postByPostHash"]>;
    const newPostStats = structuredClone(
      updatedPostRef.current?.postStats || ({} as PostStat),
    );
    const optimisticReactionTransactor =
      user && user.id
        ? {
            __typename: "Account" as const,
            id: user.id,
            publicKey: user.publicKey,
            username: user.username,
            extraData: user.extraData,
            description: user.description,
            pkid: user.pkid,
            isVerified: user.isVerified,
            creatorBasisPoints: user.creatorBasisPoints,
            totalBalanceUsdCents: user.totalBalanceUsdCents,
            daoCoinMintingDisabled: user.daoCoinMintingDisabled,
            daoCoinsInCirculationNanosHex: user.daoCoinsInCirculationNanosHex,
            isBlacklisted: user.isBlacklisted,
            subscriptionTiers: user.subscriptionTiers ?? {
              __typename: "SubscriptionTiersConnection" as const,
              totalCount: 0,
              nodes: [],
            },
            isGreylisted: user.isGreylisted ?? {
              __typename: "UserAssociationsConnection" as const,
              totalCount: 0,
            },
            isNSFW: user.isNSFW ?? {
              __typename: "UserAssociationsConnection" as const,
              totalCount: 0,
            },
            isVerifier: user.isVerifier ?? {
              __typename: "UserAssociationsConnection" as const,
              totalCount: 0,
            },
          }
        : null;
    const reactions = getUserReactions();
    const isReacted = !!reactions.find(
      (association) => association?.associationValue === associationValue,
    );

    /**
     * Handle wealth
     */
    newPostStats.upvoteWealthTotalUsdCentsCapped ||= 0;

    // Like or Un-dislike
    if (
      (!isReacted && associationValue === AssociationReactionValue.LIKE) ||
      (isReacted && associationValue === AssociationReactionValue.DISLIKE)
    ) {
      debug("Like or Un-dislike", { isReacted, associationValue });
      newPostStats.upvoteWealthTotalUsdCentsCapped += userCappedWealth;
    }
    // Unlike or Dislike
    else if (
      (isReacted && associationValue === AssociationReactionValue.LIKE) ||
      (!isReacted && associationValue === AssociationReactionValue.DISLIKE)
    ) {
      debug("Unlike or Dislike", { isReacted, associationValue });
      newPostStats.upvoteWealthTotalUsdCentsCapped -= userCappedWealth;
    }

    /**
     * Handle association and association count
     */

    const currentUserReactionNodes = [
      ...(newCurrentUserStats.currentUserReactions?.nodes ?? []),
    ];
    newCurrentUserStats.currentUserReactions = {
      __typename:
        newCurrentUserStats.currentUserReactions?.__typename ??
        "PostAssociationsConnection",
      nodes: currentUserReactionNodes,
    };

    if (!isReacted) {
      currentUserReactionNodes.push({
        __typename: "PostAssociation",
        associationType: AssociationType.reaction,
        associationValue,
        id: crypto.randomUUID(),
        associationId: crypto.randomUUID(),
        transactor:
          optimisticReactionTransactor as unknown as PostAssociation["transactor"],
        postHash,
      } as NonNullable<(typeof currentUserReactionNodes)[number]>);
      newPostStats[ReactionTypeToFieldNameMap[associationValue]] ||= 0;
      newPostStats[ReactionTypeToFieldNameMap[associationValue]] += 1;
      newPostStats.totalReactionCount ||= 0;
      newPostStats.totalReactionCount += 1;
    } else {
      newCurrentUserStats.currentUserReactions.nodes =
        currentUserReactionNodes.filter((association) => {
          const publicKey = association?.transactor?.publicKey;
          const currentAssociationValue = association?.associationValue;

          return (
            publicKey !== user?.publicKey ||
            (currentAssociationValue !== associationValue &&
              publicKey === user?.publicKey)
          );
        });
      newPostStats[ReactionTypeToFieldNameMap[associationValue]] -= 1;
      newPostStats.totalReactionCount! -= 1;
    }

    setOptimistic({
      postByPostHash: newCurrentUserStats,
    });

    // Optimistically update the post itself in the cache as it's a separate entity
    apolloClient.cache.modify({
      id: apolloClient.cache.identify(post),
      fields: {
        postStats: () => {
          return newPostStats;
        },
      },
    });

    // We update the local object also, so we don't need to wait for the cache to update
    updatedPostRef.current = structuredClone(updatedPostRef.current) || {};
    updatedPostRef.current.postStats = newPostStats;

    updatePost(updatedPostRef.current.postHash!, updatedPostRef.current);

    // Posts in the feed are stored in cache in custom state, it's hacky, but we need to update them manually
    const feedPost = posts.find((p) => p.postHash === postHash);
    if (feedPost) {
      updatePostByHash(postHash, {
        ...feedPost,
        postStats: newPostStats,
      });
    }
  };

  type handleReactionProps = {
    associationValue: AssociationReactionValue;
    updatePostRef?: boolean;
  };

  const handleReaction = ({
    associationValue,
    updatePostRef = true,
  }: handleReactionProps) => {
    if (!user?.publicKey || !post?.postHash) return;

    debug("Handling reaction", associationValue);

    if (updatePostRef) {
      updatedPostRef.current = post;
    }

    const reactions = getLatestReactions();

    /**
     * Confetti
     */
    if (
      !reactions[associationValue].isReacted &&
      associationValue !== AssociationReactionValue.LIKE &&
      associationValue !== AssociationReactionValue.DISLIKE
    ) {
      const emoji = AssociationReactionValueEmojiMap[associationValue];
      addConfetti([{ emojis: [emoji] }]);
    }

    /**
     * Remove likes/dislikes
     */
    if (
      associationValue === AssociationReactionValue.LIKE &&
      reactions[AssociationReactionValue.DISLIKE].isReacted
    ) {
      debug("Dislike is already reacted, removing it first");
      handleReaction({
        associationValue: AssociationReactionValue.DISLIKE,
        updatePostRef: false,
      });
    } else if (
      associationValue === AssociationReactionValue.DISLIKE &&
      reactions[AssociationReactionValue.LIKE].isReacted
    ) {
      debug("Like is already reacted, removing it first");
      handleReaction({
        associationValue: AssociationReactionValue.LIKE,
        updatePostRef: false,
      });
    }

    handleOptimisticUpdate({ associationValue });
    syncReactions();

    if (updatePostRef) {
      updatedPostRef.current = null;
    }
  };

  const syncReactionRequest = async () => {
    const { data } = await fetchOnly();
    const latestRemoteData = data?.postByPostHash;
    const latestLocalData = readLatestData()?.postByPostHash;
    const getCurrentUserAssociations = (associations?: PostAssociation[]) =>
      associations?.filter(
        ({ associationType }) => associationType === AssociationType.reaction,
      ) ?? [];

    const latestRemoteCurrentUserAssociations = getCurrentUserAssociations(
      latestRemoteData?.currentUserReactions?.nodes as PostAssociation[],
    );

    const latestLocalCurrentUserAssociations = getCurrentUserAssociations(
      latestLocalData?.currentUserReactions?.nodes as PostAssociation[],
    );
    const promises = [];

    for (
      let i = 0;
      latestRemoteCurrentUserAssociations[i] ||
      latestLocalCurrentUserAssociations[i];
      i++
    ) {
      const remoteAssociation = latestRemoteCurrentUserAssociations[i];
      const localAssociation = latestLocalCurrentUserAssociations[i];

      // We have an association in remote, but not in local => delete
      if (
        remoteAssociation &&
        !latestLocalCurrentUserAssociations.find(
          ({ associationValue: av }) =>
            av === remoteAssociation.associationValue,
        ) &&
        user?.publicKey &&
        remoteAssociation.associationId
      ) {
        const params = {
          TransactorPublicKeyBase58Check: user.publicKey,
          AssociationID: remoteAssociation.associationId,
        };
        debug("Triggering delete request", params);
        promises.push(deletePostAssociation(params));
      }

      // If we find an association in local, but not in remote => create
      if (
        localAssociation &&
        !latestRemoteCurrentUserAssociations.find(
          ({ associationValue }) =>
            associationValue === localAssociation.associationValue,
        ) &&
        localAssociation.associationValue &&
        post?.postHash &&
        user?.publicKey
      ) {
        const params = {
          AssociationType: AssociationType.reaction,
          AssociationValue: localAssociation.associationValue,
          PostHashHex: post.postHash,
          TransactorPublicKeyBase58Check: user.publicKey,
        };
        debug("Triggering create request", params);
        promises.push(createPostAssociation(params));
      }
    }

    return Promise.allSettled(promises);
  };

  const syncReactionsTimeout = useRef<NodeJS.Timeout | null>(null);
  const syncReactions = () => {
    if (syncReactionsTimeout.current) {
      clearTimeout(syncReactionsTimeout.current);
    }
    syncReactionsTimeout.current = setTimeout(async () => {
      setSyncing(true);
      try {
        await syncReactionRequest();
      } catch (e) {
        toast({
          variant: "destructive",
          title: "Error",
          description: `An error happened when creating post association`,
          details: createToastErrorDetails(e),
        });
      } finally {
        setSyncing(false);
      }
    }, SYNC_THROTTLE_TIMEOUT);
  };

  return {
    reactionsLoading,
    handleReaction,
    cappedWealthUsdCents: upvoteWealthTotalUsdCentsCapped,
    totalCount,
    reactions: getLatestReactions(),
    get hasAtLeastOneUserReaction() {
      const reactions = getLatestReactions();

      return Object.values(reactions).some(({ isReacted }) => isReacted);
    },
  };
};
